import type { PropertySelectionRow, GeoSpaceChiban } from "../../types";
import type { BookTypeEng } from "@/types/acquirebook";
import { findMapClickProperty } from "../popup/tags/tagUtils";
import { normalizeLocation, normalizeChibanKaokuNumber } from "../index";

/**
 * 地図クリック時の物件追加・検索処理の結果
 *
 * Args:
 *   existingTags: 既存物件に設定されているタグリスト
 *   existingRemarks: 既存物件に設定されている備考
 *   existingAdditionalFields: 既存物件に設定されている追加項目
 *   builtLocation: 構築された所在地文字列
 *   normalizedChiban: 正規化された地番文字列
 */
export interface PropertyManagementResult {
  existingTags: string[];
  existingRemarks: string;
  existingAdditionalFields: string;
  builtLocation: string;
  chiban: string;
}

/**
 * 地図クリック時の物件追加・検索処理のパラメータ
 *
 * Args:
 *   searchResult: 地図クリックで取得した地番情報
 *   selectedProperties: 現在選択されている不動産物件のリスト
 *   setSelectedProperties: 不動産物件リストを更新するセッター関数
 *   setCurrentPopupRowId: ポップアップに表示中の物件行IDを設定するセッター
 *   setPopupInfo: ポップアップ情報を設定するセッター
 *   buildLocationString: 地番情報から所在地文字列を構築する関数
 *   checkDuplicateProperties: 物件の重複をチェックする関数
 */
export interface AddOrFindMapClickPropertyParams {
  searchResult: GeoSpaceChiban;
  selectedProperties: PropertySelectionRow[];
  setSelectedProperties: React.Dispatch<
    React.SetStateAction<PropertySelectionRow[]>
  >;
  setCurrentPopupRowId: React.Dispatch<React.SetStateAction<number | null>>;
  setPopupInfo: React.Dispatch<
    React.SetStateAction<{
      location: string;
      chiban: string;
      prefName: string;
    } | null>
  >;
  buildLocationString: (r: GeoSpaceChiban) => string;
  findDuplicatePropertyIndex: (
    rows: PropertySelectionRow[],
    bookType: BookTypeEng,
    prefName: string,
    location: string,
    chiban: string
  ) => number;
  mergePropertySelectionRow: (
    rows: PropertySelectionRow[],
    index: number,
    area: number | null,
    rawData?: GeoSpaceChiban
  ) => PropertySelectionRow[] | undefined;
  /** 左下の物件選択リストへ追加するか。無料ユーザーはリストが無効化されているため false。 */
  addToList: boolean;
}

/**
 * 地図クリック時に物件を追加または既存物件を検索する
 *
 * 地図クリックで取得した地番情報を基に、物件選択テーブルに新しい物件を追加するか、既存の重複物件を特定する。
 * ポップアップの表示制御とタグ管理も行う。
 *
 * Args:
 *   params: AddOrFindMapClickPropertyParams - 処理に必要なパラメータ
 *
 * Returns:
 *   PropertyManagementResult: 処理結果（既存タグ、所在地、地番）
 */
export const addOrFindMapClickProperty = ({
  searchResult,
  selectedProperties,
  setSelectedProperties,
  setCurrentPopupRowId,
  setPopupInfo,
  buildLocationString,
  findDuplicatePropertyIndex,
  mergePropertySelectionRow,
  addToList,
}: AddOrFindMapClickPropertyParams): PropertyManagementResult => {
  const bookType = "LAND" as BookTypeEng;
  const builtLocation = normalizeLocation(buildLocationString(searchResult));
  const chiban = normalizeChibanKaokuNumber(searchResult.chiban);

  // 無料ユーザーは左下の物件選択リストが無効化されているため、リストへ追加しない。
  // ポップアップ表示に必要な所在地・地番のみ返し、リスト／ポップアップ管理の状態
  // （selectedProperties・currentPopupRowId・popupInfo）は変更しない。
  if (!addToList) {
    return {
      existingTags: [],
      existingRemarks: "",
      existingAdditionalFields: "",
      builtLocation,
      chiban,
    };
  }

  // 既存物件を検索（タグ・備考の取得とID設定に使用）
  const existingRow = findMapClickProperty(
    selectedProperties,
    builtLocation,
    chiban,
    searchResult.prefName
  );

  // 物件の重複をチェック（同一都道府県・所在地・地番の物件が既に存在するか）
  const duplicateIndex = findDuplicatePropertyIndex(
    selectedProperties,
    bookType,
    searchResult.prefName,
    builtLocation,
    chiban
  );

  const isDuplicate = duplicateIndex !== -1;

  if (!isDuplicate) {
    // 重複していない場合：新しい物件を物件選択テーブルに追加
    setSelectedProperties((prev) => {
      const lastId =
        prev.length > 0 ? Math.max(...prev.map((row) => row.id)) : 0;
      const newRowId = lastId + 1;
      setCurrentPopupRowId(newRowId);

      const newRow = {
        id: newRowId,
        selected: true,
        bookType,
        prefName: searchResult.prefName,
        location: builtLocation,
        chibanKaokuNumber: chiban,
        area: searchResult.area,
        isFeedOrigin: false,
        rawData: searchResult,
        tags: [],
        remarks: "",
        additionalFields: "",
        pinType: "map-click" as const,
      };
      return [...prev, newRow].sort((a, b) => b.id - a.id);
    });
  } else {
    setSelectedProperties((prev) => {
      const targetIndex = findDuplicatePropertyIndex(
        prev,
        bookType,
        searchResult.prefName,
        builtLocation,
        chiban
      );

      if (targetIndex === -1) return prev;

      const merged =
        mergePropertySelectionRow(
          prev,
          targetIndex,
          searchResult.area,
          searchResult
        ) ?? prev;

      // 青ピンを表示した行として印を付ける。
      //
      // 範囲検索や CSV 取込で追加された行は pinType を持たない。印が無いままだと、
      // 「表示中の青ピンに対応する行が selectedProperties に存在するか」を
      // pinType === "map-click" で判定している箇所（青ピンの消去判定・タグ／備考の
      // 保存先の特定）がリンク無しと見なし、ポップアップを開いた直後に青ピンごと
      // 消えてしまう。
      //
      // 受付帳ピン等、既に別の pinType を持つ行は書き換えない。それぞれのレイヤーが
      // pinType で行を絞り込んでいるため、上書きするとそのピンがリストから外れる。
      const target = merged[targetIndex];
      if (!target || target.pinType !== undefined) return merged;

      const withPinType = [...merged];
      withPinType[targetIndex] = { ...target, pinType: "map-click" };
      return withPinType;
    });
    // 重複している場合：既存物件の行IDを取得してポップアップ管理に使用
    setCurrentPopupRowId(existingRow?.id ?? null);
  }

  // ポップアップ情報を設定（タグ管理機能で使用）
  setPopupInfo({
    location: builtLocation,
    chiban,
    prefName: searchResult.prefName,
  });

  // 既存物件のタグと備考と追加項目を取得（ポップアップの入力欄に表示するため）
  const existingTags: string[] = existingRow?.tags ?? [];
  const existingRemarks: string = existingRow?.remarks ?? "";
  const existingAdditionalFields: string = existingRow?.additionalFields ?? "";

  return {
    existingTags,
    existingRemarks,
    existingAdditionalFields,
    builtLocation,
    chiban,
  };
};
